import { PropBotGrowthService } from './prop-bot-growth-service';
import { PropBotDetails } from '@/types/prop-bots';

export interface EnhancedPropBotStats {
  winRate: number;
  maxDrawdown: number;
  potentialProfit: number;
  passRate: number;
  estimatedPassTime: number;
  connectedAccountsTotal: number;
  dataSource: {
    winRate: 'growth_chart' | 'manual';
    maxDrawdown: 'growth_chart' | 'manual';
    potentialProfit: 'growth_chart' | 'manual';
  };
}

export class EnhancedPropBotStatsService {
  /**
   * Get enhanced statistics prioritizing Growth Chart data over manual entries
   */
  static async getEnhancedStats(
    botId: string,
    propDetails?: PropBotDetails
  ): Promise<EnhancedPropBotStats> {
    // Helper to parse values that might be strings with % or numbers
    const parseValue = (value: string | number | undefined): number => {
      if (!value) return 0;
      if (typeof value === 'number') return value;
      // Remove % sign if exists and parse
      return parseFloat(value.toString().replace('%', '')) || 0;
    };

    // Initialize with manual values as fallback
    let stats: EnhancedPropBotStats = {
      winRate: parseValue(propDetails?.winrate),
      maxDrawdown: parseValue(propDetails?.max_drawdown),
      potentialProfit: parseValue(propDetails?.potential_profit),
      passRate: parseValue(propDetails?.pass_rate),
      estimatedPassTime: parseValue(propDetails?.estimated_pass_time),
      connectedAccountsTotal: propDetails?.connected_accounts_total || 0,
      dataSource: {
        winRate: 'manual',
        maxDrawdown: 'manual',
        potentialProfit: 'manual',
      }
    };

    try {
      // Try to get Growth Chart data
      const growthData = await PropBotGrowthService.getPropBotGrowthData(botId);
      
      // Determine which growth data to use (prioritize merged > original > shadow)
      let selectedGrowthStats = null;
      let minCapital = propDetails?.min_capital || 100000;

      if (growthData.hasMergedAnalysis && growthData.merged?.stats) {
        selectedGrowthStats = growthData.merged.stats;
      } else if (growthData.originalBot?.stats && growthData.originalBot.stats.totalTrades > 0) {
        selectedGrowthStats = growthData.originalBot.stats;
      } else if (growthData.shadowBot?.stats && growthData.shadowBot.stats.totalTrades > 0) {
        selectedGrowthStats = growthData.shadowBot.stats;
      }

      if (selectedGrowthStats && selectedGrowthStats.totalTrades > 0) {
        // Calculate Win Rate from Growth Chart
        if (selectedGrowthStats.winRate > 0) {
          stats.winRate = Math.round(selectedGrowthStats.winRate * 100) / 100;
          stats.dataSource.winRate = 'growth_chart';
        }

        // Calculate Max Drawdown as percentage of min capital
        if (selectedGrowthStats.maxDrawdownDollar && minCapital > 0) {
          stats.maxDrawdown = Math.round((Math.abs(selectedGrowthStats.maxDrawdownDollar) / minCapital) * 10000) / 100;
          stats.dataSource.maxDrawdown = 'growth_chart';
        }

        // Calculate Total Profit as percentage of capital
        if (selectedGrowthStats.finalProfit !== undefined && minCapital > 0) {
          const totalProfitPercentage = this.calculateTotalProfitPercentage(
            selectedGrowthStats.finalProfit,
            minCapital
          );
          console.log(`🔍 Debug Enhanced Stats for botId ${botId}:`, {
            finalProfit: selectedGrowthStats.finalProfit,
            minCapital,
            totalProfitPercentage,
            rounded: Math.round(totalProfitPercentage * 100) / 100
          });
          stats.potentialProfit = Math.round(totalProfitPercentage * 100) / 100;
          stats.dataSource.potentialProfit = 'growth_chart';
        }
      }
    } catch (error) {
      console.error('Error fetching Growth Chart data for enhanced stats:', error);
      // Continue with manual values as fallback
    }

    return stats;
  }

  /**
   * Calculate total profit percentage from Growth Chart data
   */
  private static calculateTotalProfitPercentage(
    finalProfit: number,
    minCapital: number
  ): number {
    if (minCapital === 0) return 0;

    // Calculate total profit as percentage of capital
    const totalProfitPercentage = (finalProfit / minCapital) * 100;

    return totalProfitPercentage; // Can be positive or negative
  }

  /**
   * Get data source indicators for UI display
   */
  static getDataSourceLabels(dataSource: EnhancedPropBotStats['dataSource']) {
    return {
      winRate: dataSource.winRate === 'growth_chart' ? '📈' : '📝',
      maxDrawdown: dataSource.maxDrawdown === 'growth_chart' ? '📈' : '📝',
      potentialProfit: dataSource.potentialProfit === 'growth_chart' ? '📈' : '📝',
    };
  }
}